import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Header, Icon, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";
import { doc, onSnapshot } from "firebase/firestore";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { db } from "../../app/config/firebase";
import { stripePromise } from "../../app/config/stripe";
import { formatPrice } from "../../app/common/util/formatPrice";
import {
  functionGetManagerStrategyCreditProducts,
  functionCreateManagerStrategyCreditCheckout,
} from "../../app/firestore/functionsService";

const NAVY = "#16243c";
const BLUE = "#2185d0";
const LIGHT_BLUE_BG = "#eaf4fc";
const LIGHT_BLUE_BORDER = "#cfe4f7";
const GREY_TEXT = "#767676";

// How long to keep watching the manager's pool balance doc for the webhook-
// driven update before giving up and just telling the manager to check back
// - mirrors TransactionStrategyDashboard.jsx's identical per-agent pattern
// (CREDIT_FULFILLMENT_WAIT_MS) exactly.
const CREDIT_FULFILLMENT_WAIT_MS = 45000;

// Manager-only "Buy Strategy Credits for Your Agents" section - lives on the
// "AI Credits" page (ManagerStrategyCreditsDashboard.jsx) right alongside
// the shared-pool balance it tops up, rather than the general Payments page,
// so a manager sees "here's my balance" and "buy more" in the same place.
// Mirrors StrategyCreditConfirmModal.jsx's BuyStrategyCreditsSection
// (per-agent personal credits) as closely as possible so the two purchase
// flows feel like the same product - same "server is the only source of
// truth for price/credits" principle, same embedded-Checkout-then-wait-for-
// webhook pattern, just a different checkout callable and a different
// (manager_pool) balance doc to watch afterward.
//
// onPurchaseFulfilled: optional callback invoked once the webhook-driven
// balance update is actually observed, so the parent page's own balance
// display (which this component doesn't render itself) can refetch and show
// the new total immediately instead of requiring a manual page reload.
export default function ManagerStrategyCreditsSection({ onPurchaseFulfilled }) {
  const uid = useSelector((state) => state.auth.currentUser?.uid);
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [buyingPackageKey, setBuyingPackageKey] = useState(null);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null); // "pending" | "fulfilled" | "timeout" | null
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    functionGetManagerStrategyCreditProducts()
      .then((data) => {
        if (isMountedRef.current) setProducts(data?.products || []);
      })
      .catch((error) => {
        if (isMountedRef.current) setLoadError(error?.message || "Unable to load Strategy Credit purchase options.");
      });
  }, []);

  async function handleBuy(packageKey) {
    setBuyingPackageKey(packageKey);
    try {
      const { clientSecret } = await functionCreateManagerStrategyCreditCheckout({ packageKey });
      if (isMountedRef.current) setCheckoutClientSecret(clientSecret);
    } catch (error) {
      toast.error(error?.message || "Unable to start checkout right now.");
    } finally {
      if (isMountedRef.current) setBuyingPackageKey(null);
    }
  }

  // Fires once Stripe confirms the payment client-side (Embedded Checkout's
  // onComplete) - NOT the same as the credit actually landing, which only
  // Stripe's webhook can do server-side
  // (functions/strategyCredits/payments/webhook.js). This just watches the
  // manager's own strategyCreditBalances doc (readable directly per
  // firebase.rules, since its ownerId is the manager's own uid) for that
  // webhook's write.
  function handleCheckoutComplete() {
    setCheckoutClientSecret(null);
    setPurchaseStatus("pending");
    let settled = false;

    const unsubscribe = onSnapshot(
      doc(db, "strategyCreditBalances", `manager_pool_${uid}`),
      (snap) => {
        const data = snap.data();
        const updatedAtMillis = data?.updatedAt?.toMillis ? data.updatedAt.toMillis() : null;
        const isRecentUpdate = updatedAtMillis && Date.now() - updatedAtMillis < 3 * 60 * 1000;
        if (settled || !isRecentUpdate) return;

        settled = true;
        clearTimeout(timeoutId);
        setPurchaseStatus("fulfilled");
        toast.success("Strategy Credits added to your shared pool.");
        if (onPurchaseFulfilled) onPurchaseFulfilled();
      },
      () => {
        settled = true;
        setPurchaseStatus("timeout");
      }
    );

    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      setPurchaseStatus("timeout");
    }, CREDIT_FULFILLMENT_WAIT_MS);
  }

  // Still loading and nothing has gone wrong yet - render nothing rather
  // than flashing an empty section before the catalog resolves.
  if (products === null && !loadError) return null;

  if (checkoutClientSecret) {
    return (
      <Segment clearing style={{ marginBottom: 24 }}>
        <Header as="h2" color="blue">
          Buy Strategy Credits for Your Agents
        </Header>
        <Button
          basic
          size="tiny"
          icon="arrow left"
          content="Back"
          onClick={() => setCheckoutClientSecret(null)}
          style={{ marginBottom: 10 }}
        />
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret: checkoutClientSecret, onComplete: handleCheckoutComplete }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Segment>
    );
  }

  return (
    <Segment clearing style={{ marginBottom: 24 }}>
      <Header as="h2" color="blue">
        Buy Strategy Credits for Your Agents
      </Header>
      <p className="text-grey">
        Purchase AI Strategy Credits into your shared pool - any agent you've enabled sharing for can use
        them when generating a Pricing Strategy, Listing Description, or other Intelligent Strategy.
      </p>

      {purchaseStatus === "pending" && (
        <Segment secondary>
          <Icon name="circle notched" loading /> Updating your shared pool balance&hellip;
        </Segment>
      )}
      {purchaseStatus === "fulfilled" && (
        <Segment secondary color="green">
          <Icon name="check circle" color="green" /> Strategy Credits added to your shared pool.
        </Segment>
      )}
      {purchaseStatus === "timeout" && (
        <Segment secondary color="yellow">
          <Icon name="clock outline" /> Your payment was received but is taking longer than usual to reflect
          here.{" "}
          {onPurchaseFulfilled ? (
            <Button basic size="small" onClick={() => onPurchaseFulfilled()}>
              Check my balance again
            </Button>
          ) : (
            "Refresh the page in a moment, or contact support if it doesn't update."
          )}
        </Segment>
      )}

      {loadError && <p className="text-medium text-grey">Strategy Credit purchases aren't available right now.</p>}
      {!loadError && products?.length === 0 && (
        <p className="text-medium text-grey">No manager Strategy Credit packages are configured right now.</p>
      )}
      {!loadError && products?.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {products.map((product) => (
            <div
              key={product.packageKey}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 14,
                background: LIGHT_BLUE_BG,
                border: `1px solid ${LIGHT_BLUE_BORDER}`,
                borderRadius: 10,
                padding: "14px 18px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <Icon name="users" size="big" color="blue" style={{ margin: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, color: NAVY }}>
                    {product.label} &mdash; {formatPrice(product.amount, product.currency)}
                  </div>
                  <div style={{ color: GREY_TEXT, fontSize: "0.8em", marginTop: 2, maxWidth: 420 }}>
                    Added to your shared pool, available to any agent you've enabled Strategy Credit sharing for.
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <Button
                  primary
                  loading={buyingPackageKey === product.packageKey}
                  disabled={!!buyingPackageKey}
                  onClick={() => handleBuy(product.packageKey)}
                  content="Buy Credits"
                />
                <div style={{ color: GREY_TEXT, fontSize: "0.75em", marginTop: 4 }}>TransActioner Pro Intelligent Strategy Credits</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ color: BLUE, fontSize: "0.78em", marginTop: 10 }}>
        <Icon name="shield" /> Secure checkout handled entirely by Stripe.
      </div>
    </Segment>
  );
}
